import { INVESTOR_TEST_ELEMENT_ID } from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { StringInput as StringInputV2 } from '@dealstack/frontend/shared/components/forms/StringInput/StringInput.v2';
import { useJoinAndAllocationOtherDetails } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/useJoinAndAllocationOtherDetails';
import { EMPTY_CALLBACK } from '@dealstack/shared/lib/utils';
import { FormProvider } from 'react-hook-form';
import { InputsWrapper } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/styled';
import { TaxResidencesSection } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/TaxResidencesSection';
import { EmploymentSection } from '@dealstack/frontend/investor/pages/Investment/InvestmentJoinAndAllocation/JoinAndAllocationPersonalDetails/JoinAndAllocationOtherDetails/EmploymentSection';
import { MultiSelect, Select, TextInput } from 'react-hook-form-mantine';
import {
	COUNTRIES_NAME_CODE_GROUPED_OPTIONS,
	COUNTRIES_NATIONALITY_CODE_OPTION,
} from '@dealstack/shared/lib/countries/countriesData';
import { groupedSelectDividerStyles } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling';
import { Text } from '@mantine/core';
import { useTranslationInvestor } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';

export const JoinAndAllocationOtherDetails = ({ className }: { className?: string }) => {
	const tInvestor = useTranslationInvestor();

	const {
		customDetailsForm,
		onSubmit,
		isPIVInvestment,
		personalFields,
		pivFields,
		savedTaxResidences,
		showTaxDetails,
		showTaxSection,
		showPlaceOfBirth,
		showCountryOfBirth,
		showEmployment,
		employerOptions,
		showCitizenship,
		placeOfBirthFieldName,
		countryOfBirthFieldName,
		citizenshipFieldName,
	} = useJoinAndAllocationOtherDetails();

	const showPersonalOtherDetails = !!personalFields?.length;
	const showPivOtherDetails = isPIVInvestment && !!pivFields?.length;
	const showOtherDetails = showPersonalOtherDetails || showPivOtherDetails;

	return (
		<div className={className}>
			<FormProvider {...customDetailsForm}>
				<form onSubmit={customDetailsForm.handleSubmit(onSubmit)}>
					{showTaxSection && (
						<TaxResidencesSection
							showTin={showTaxDetails}
							savedTaxResidences={savedTaxResidences}
						/>
					)}

					{showEmployment && <EmploymentSection employerOptions={employerOptions} />}

					{(showOtherDetails ||
						showPlaceOfBirth ||
						showCountryOfBirth ||
						showCitizenship) && (
						<Text size="B5Bold" mb="1rem" mt="2rem">
							{tInvestor('investment.personal_details.other')}
						</Text>
					)}

					<InputsWrapper>
						{showPlaceOfBirth && placeOfBirthFieldName && (
							<TextInput
								name={placeOfBirthFieldName}
								label={tInvestor('investment.personal_details.place_of_birth')}
								placeholder={tInvestor(
									'investment.personal_details.place_of_birth'
								)}
								size="md"
							/>
						)}
						{showCountryOfBirth && countryOfBirthFieldName && (
							<Select
								name={countryOfBirthFieldName}
								label={tInvestor('investment.personal_details.country_of_birth', {
									defaultValue: 'Country of birth',
								})}
								placeholder={tInvestor(
									'investment.personal_details.country_of_birth',
									{ defaultValue: 'Country of birth' }
								)}
								data={COUNTRIES_NAME_CODE_GROUPED_OPTIONS}
								searchable
								size="md"
								styles={groupedSelectDividerStyles}
							/>
						)}
						{showCitizenship && citizenshipFieldName && (
							<MultiSelect
								name={citizenshipFieldName}
								label={tInvestor('investment.personal_details.citizenship')}
								placeholder={tInvestor('investment.personal_details.citizenship')}
								data={COUNTRIES_NATIONALITY_CODE_OPTION}
								searchable
								size="md"
							/>
						)}
						{showPersonalOtherDetails && (
							<>
								{personalFields.map((field) => (
									<StringInputV2
										data-testid={
											INVESTOR_TEST_ELEMENT_ID.INVESTOR__PERSONAL_DETAILS_OTHER
										}
										hint={field.instruction}
										label={field.name}
										inputPlaceHolder={field.name}
										name={field.id}
										key={field.id}
										onInputChange={EMPTY_CALLBACK}
										size="m"
									/>
								))}
							</>
						)}

						{showPivOtherDetails &&
							pivFields.map((field) => (
								<StringInputV2
									data-testid={
										INVESTOR_TEST_ELEMENT_ID.INVESTOR_PIV_PERSONAL_DETAILS_OTHER
									}
									hint={field.instruction}
									label={field.name}
									name={field.id}
									key={field.id}
									onInputChange={EMPTY_CALLBACK}
									size="m"
									inputPlaceHolder="..."
								/>
							))}
					</InputsWrapper>
				</form>
			</FormProvider>
		</div>
	);
};
